import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import getLEPackageItemTotals from '../accommodation/getLEPackageItemTotals'
import type { LereCacheContext } from '../accommodation/getLEPackageItemTotals'
import { getItemUniqueKey } from '../accommodation/cart'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { applyCancellationOptionPriceFactorToPrices } from 'lib/offer/cancellationOptionPricing'

function getAccommodationItemViewLoyaltyUpgradeAvailability(
  offer: App.Offer,
  item: App.Checkout.LEAccommodationItem,
  calendarsByOccupancy: App.CalendarState['calendarsByOccupancy'],
  availableRatesByOccupancy: { [offerId: string]: App.OfferAvailableRatesByOccupancy },
  lereCacheContext?: LereCacheContext,
): App.Checkout.AccommodationItemViewLoyaltyUpgradeAvailability | undefined {
  if (!isInstantBookingLEHotelItem(item)) return

  const uniqueKey = getItemUniqueKey(item)
  const originPackage = offer.packages.find(pkg => pkg.uniqueKey === uniqueKey)

  if (!originPackage || !isHotelPackage(originPackage)) return

  const priceFactor = item.cancellationOptionPriceFactor
  const originPackageTotals = getLEPackageItemTotals(
    offer,
    originPackage,
    item.checkIn,
    item.checkOut,
    item.occupancy,
    undefined,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    lereCacheContext,
  )
  const originTotals = originPackageTotals && applyCancellationOptionPriceFactorToPrices(originPackageTotals, priceFactor)

  if (!originTotals) return

  const originPrimaryTitle = originPackage.roomType.name

  const roomUpgradePackageUniqueKey = originPackage.loyalty.upgradeTargetsUniqueKeys?.room
  const roomUpgradePackage = roomUpgradePackageUniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === roomUpgradePackageUniqueKey) : undefined
  const roomUpgradePrimaryTitle = roomUpgradePackage?.roomType?.name ?? ''
  const roomUpgradePackageTotals = roomUpgradePackage ? getLEPackageItemTotals(
    offer,
    roomUpgradePackage,
    item.checkIn,
    item.checkOut,
    item.occupancy,
    undefined,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    lereCacheContext,
  ) : undefined
  const roomUpgradeTotals = roomUpgradePackageTotals && applyCancellationOptionPriceFactorToPrices(roomUpgradePackageTotals, priceFactor)

  const packageUpgradePackageUniqueKey = originPackage.loyalty.upgradeTargetsUniqueKeys?.package
  const packageUpgradePackage = packageUpgradePackageUniqueKey ? offer.packages.find(pkg => pkg.uniqueKey === packageUpgradePackageUniqueKey) : undefined
  const packageUpgradePrimaryTitle = packageUpgradePackage?.roomType?.name ?? ''
  const packageUpgradePackageTotals = packageUpgradePackage ? getLEPackageItemTotals(
    offer,
    packageUpgradePackage,
    item.checkIn,
    item.checkOut,
    item.occupancy,
    undefined,
    calendarsByOccupancy,
    availableRatesByOccupancy,
    lereCacheContext,
  ) : undefined
  const packageUpgradeTotals = packageUpgradePackageTotals && applyCancellationOptionPriceFactorToPrices(packageUpgradePackageTotals, priceFactor)

  if (!roomUpgradePackage && !packageUpgradePackage) return

  if (
    (roomUpgradePackage && roomUpgradeTotals?.isAvailable && isHotelPackage(roomUpgradePackage)) &&
    (packageUpgradePackage && packageUpgradeTotals?.isAvailable && isHotelPackage(packageUpgradePackage)
    )) {
    return {
      upgradeAvailabilityType: 'room-and-package',
      originPackage,
      originTotals,
      originPrimaryTitle,
      roomUpgradePackage,
      roomUpgradeTotals,
      roomUpgradePrimaryTitle,
      packageUpgradePackage,
      packageUpgradeTotals,
      packageUpgradePrimaryTitle,
    }
  }

  if (packageUpgradePackage && packageUpgradeTotals?.isAvailable && isHotelPackage(packageUpgradePackage)) {
    return {
      upgradeAvailabilityType: 'package-only',
      originPackage,
      originTotals,
      originPrimaryTitle,
      packageUpgradePackage,
      packageUpgradeTotals,
      packageUpgradePrimaryTitle,
    }
  }

  if (roomUpgradePackage && roomUpgradeTotals?.isAvailable && isHotelPackage(roomUpgradePackage)) {
    return {
      upgradeAvailabilityType: 'room-only',
      originPackage,
      originTotals,
      originPrimaryTitle,
      roomUpgradePackage,
      roomUpgradeTotals,
      roomUpgradePrimaryTitle,
    }
  }
}

export default getAccommodationItemViewLoyaltyUpgradeAvailability
